import { useState } from "react";
import Card from "components/card";
import InputField from "components/fields/InputField";
import { apiPatch, apiPost } from "lib/apiClient";
import {
  DESCRIPTION_MAX,
  isHexColour,
  normalizeSlugInput,
  partnerPageUrls,
  profileErrorField,
  profileForm,
  profilePatch,
  profileProblems,
  slugFromName,
} from "lib/partnerPages";

const textareaClass =
  "mt-2 w-full rounded-sm border bg-background px-3 py-2 text-sm text-foreground outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus:border-ring focus:ring-[3px] focus:ring-ring/50";
const fileInputClass =
  "block w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-gray-100 file:px-3 file:py-2 file:text-xs file:font-bold file:text-navy-700 dark:text-gray-300 dark:file:bg-navy-700 dark:file:text-white";

function profileErrorMessage(err) {
  switch (err?.code) {
    case "INVALID_PARTNER_SLUG":
      return "That address is not allowed. Use lowercase letters, numbers and hyphens, and not a reserved word such as \"dashboard\" or \"api\".";
    case "PARTNER_SLUG_TAKEN":
      return "Another partner already uses this address. Choose a different one.";
    case "INVALID_BRAND_COLOUR":
      return "Brand colours must look like #1a2b3c.";
    case "PARTNER_CLOSED":
      return "This partner is closed. Closed partners can only be viewed.";
    case "FORBIDDEN":
      return "Only a tenant administrator can change a partner's public page.";
    default:
      return err?.message || "Could not save the public page. Try again.";
  }
}

export function ColourField({ label, value, onChange, error }) {
  const valid = isHexColour(value);
  return (
    <div>
      <label className="ml-0.5 text-sm font-medium text-foreground">{label}</label>
      <div className="mt-2 flex items-center gap-2">
        <input
          type="color"
          aria-label={`${label} picker`}
          value={valid ? value.toLowerCase() : "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-12 shrink-0 cursor-pointer rounded-sm border border-border bg-background p-1"
        />
        <input
          type="text"
          aria-label={label}
          value={value}
          placeholder="Not set"
          maxLength={7}
          onChange={(e) => onChange(e.target.value.trim())}
          className={`h-10 w-full rounded-sm border bg-background px-3 font-mono text-sm text-foreground outline-none focus:border-ring focus:ring-[3px] focus:ring-ring/50 ${error ? "border-no" : "border-border"}`}
        />
        {value && (
          <button type="button" onClick={() => onChange("")} className="text-xs font-semibold text-gray-500 hover:text-red-500">
            Clear
          </button>
        )}
      </div>
      {error && <p className="ml-0.5 mt-1 text-xs font-medium text-red-500">{error}</p>}
    </div>
  );
}

export function LogoField({ label, hint, url, dark, busy, onPick, onRemove }) {
  return (
    <div>
      <p className="ml-0.5 text-sm font-medium text-foreground">{label}</p>
      <div className="mt-2 flex items-center gap-3">
        <div
          className={`flex h-16 w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg border ${
            dark ? "border-white/10 bg-navy-900" : "border-gray-200 bg-white"
          }`}
        >
          {url ? <img src={url} alt={`${label} preview`} className="max-h-14 max-w-[6.5rem] object-contain" /> : <span className={`text-xs ${dark ? "text-gray-400" : "text-gray-500"}`}>No logo</span>}
        </div>
        <div className="min-w-0 flex-1">
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) onPick(file);
            }}
            className={fileInputClass}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{busy ? "Uploading…" : hint}</p>
        </div>
        {url && !busy && (
          <button type="button" onClick={onRemove} className="text-xs font-semibold text-gray-500 hover:text-red-500">
            Remove
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Edits a partner's public page: address (slug), description, logos and brand colours.
 * Logos upload straight away (they need their own request); the rest saves with "Save".
 */
export default function PartnerProfileEditor({ partner: initial, onChanged, onClose }) {
  const [partner, setPartner] = useState(initial);
  const [form, setForm] = useState(() => profileForm(initial));
  const [problems, setProblems] = useState({});
  const [apiError, setApiError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(null);
  const [logoError, setLogoError] = useState(null);
  const [saved, setSaved] = useState(null);

  const urls = partnerPageUrls(form.slug.trim());
  const patch = profilePatch(partner, form);
  const dirty = Object.keys(patch).length > 0;

  function set(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
    setProblems((p) => ({ ...p, [key]: undefined }));
    setApiError(null);
    setSaved(null);
  }

  async function save() {
    const found = profileProblems(form);
    setProblems(found);
    setApiError(null);
    setSaved(null);
    if (Object.keys(found).length > 0 || !dirty) return;
    setBusy(true);
    try {
      const result = await apiPatch(`/portal/partners/${partner.id}/profile`, patch);
      const next = result?.partner ? { ...partner, ...result.partner } : { ...partner, ...patch };
      setPartner(next);
      setForm(profileForm(next));
      setSaved("Public page saved.");
      onChanged?.();
    } catch (err) {
      setApiError({ field: profileErrorField(err?.code), message: profileErrorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  async function uploadLogo(variant, file) {
    setLogoError(null);
    setLogoBusy(variant);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const result = await apiPost(`/portal/partners/${partner.id}/logo?variant=${variant}`, formData);
      if (result?.partner) setPartner((p) => ({ ...p, logoUrl: result.partner.logoUrl, logoDarkUrl: result.partner.logoDarkUrl }));
      onChanged?.();
    } catch (err) {
      setLogoError(err?.message || "Could not upload that logo.");
    } finally {
      setLogoBusy(null);
    }
  }

  async function removeLogo(variant) {
    setLogoError(null);
    setLogoBusy(variant);
    const key = variant === "dark" ? "logoDarkUrl" : "logoUrl";
    try {
      const result = await apiPatch(`/portal/partners/${partner.id}/profile`, { [key]: null });
      setPartner((p) => ({ ...p, [key]: result?.partner ? result.partner[key] : null }));
      onChanged?.();
    } catch (err) {
      setLogoError(profileErrorMessage(err));
    } finally {
      setLogoBusy(null);
    }
  }

  const slugError = problems.slug || (apiError?.field === "slug" ? apiError.message : null);
  const brandApiError = apiError?.field === "brand" ? apiError.message : null;
  const generalError = apiError && !apiError.field ? apiError.message : null;
  const descriptionLength = form.description.trim().length;

  return (
    <Card extra="p-5 mb-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-lg font-semibold text-navy-700 dark:text-white">{partner.name}'s public page</p>
          <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-gray-300">
            The page traders see on the storefront and on the partner's own subdomain. Leave the address empty to keep the partner off the storefront.
          </p>
        </div>
        <button type="button" onClick={onClose} className="rounded-sm border border-gray-200 px-4 py-2 text-sm font-semibold text-navy-700 dark:border-white/10 dark:text-white">
          Close
        </button>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="flex flex-col gap-4">
          <div>
            <InputField
              label="Page address"
              placeholder={slugFromName(partner.name) || "e.g. kenya-times"}
              value={form.slug}
              maxLength={63}
              state={slugError ? "error" : undefined}
              onChange={(e) => set("slug", normalizeSlugInput(e.target.value))}
            />
            {slugError ? (
              <p className="ml-0.5 mt-1 text-xs font-medium text-red-500">{slugError}</p>
            ) : urls ? (
              <div className="ml-0.5 mt-1 space-y-0.5 text-xs text-gray-500">
                <p>
                  Page: <span className="font-mono text-navy-700 dark:text-white">{urls.page}</span>
                </p>
                <p>
                  Subdomain: <span className="font-mono text-navy-700 dark:text-white">{urls.subdomain}</span>
                </p>
              </div>
            ) : (
              <p className="ml-0.5 mt-1 text-xs text-gray-500">
                No address: this partner has no public page.
                {!form.slug && slugFromName(partner.name) && (
                  <button type="button" onClick={() => set("slug", slugFromName(partner.name))} className="ml-1 font-semibold text-brand-500">
                    Use "{slugFromName(partner.name)}"
                  </button>
                )}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="partner-description" className="ml-0.5 text-sm font-medium text-foreground">
              Description
            </label>
            <textarea
              id="partner-description"
              rows={4}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="One or two sentences about the partner, shown at the top of their page."
              className={`${textareaClass} ${problems.description ? "border-no" : "border-border"}`}
            />
            <p className={`ml-0.5 mt-1 text-xs ${descriptionLength > DESCRIPTION_MAX ? "font-medium text-red-500" : "text-gray-500"}`}>
              {problems.description || `${descriptionLength} / ${DESCRIPTION_MAX}`}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <ColourField label="Primary colour" value={form.brandPrimary} onChange={(v) => set("brandPrimary", v)} error={problems.brandPrimary} />
            <ColourField label="Accent colour" value={form.brandAccent} onChange={(v) => set("brandAccent", v)} error={problems.brandAccent} />
          </div>
          {brandApiError && <p className="-mt-2 ml-0.5 text-xs font-medium text-red-500">{brandApiError}</p>}
          {(isHexColour(form.brandPrimary) || isHexColour(form.brandAccent)) && (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>Preview</span>
              <span className="rounded-sm px-3 py-1.5 text-xs font-semibold text-white" style={{ backgroundColor: isHexColour(form.brandPrimary) ? form.brandPrimary : "#64748b" }}>
                Trade
              </span>
              <span
                className="rounded-full border px-2 py-0.5 text-xs font-semibold"
                style={{ borderColor: isHexColour(form.brandAccent) ? form.brandAccent : "#94a3b8", color: isHexColour(form.brandAccent) ? form.brandAccent : "#64748b" }}
              >
                {partner.name}
              </span>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <LogoField
            label="Logo for light backgrounds"
            hint="PNG, SVG, JPEG, WebP or GIF, up to 5MB. Uploads straight away."
            url={partner.logoUrl}
            busy={logoBusy === "light"}
            onPick={(file) => uploadLogo("light", file)}
            onRemove={() => removeLogo("light")}
          />
          <LogoField
            label="Logo for dark backgrounds"
            hint="Used in dark mode. Without it, the light logo is used."
            url={partner.logoDarkUrl}
            dark
            busy={logoBusy === "dark"}
            onPick={(file) => uploadLogo("dark", file)}
            onRemove={() => removeLogo("dark")}
          />
          {logoError && <p className="text-xs font-medium text-red-500">{logoError}</p>}
        </div>
      </div>

      {generalError && <p className="mt-4 text-sm font-medium text-red-500">{generalError}</p>}
      {saved && <p className="mt-4 text-sm font-medium text-green-600 dark:text-green-400">{saved}</p>}
      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={save}
          disabled={busy || !dirty}
          className="rounded-sm bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
        >
          {busy ? "Saving…" : "Save public page"}
        </button>
        {dirty && !busy && (
          <button
            type="button"
            onClick={() => {
              setForm(profileForm(partner));
              setProblems({});
              setApiError(null);
            }}
            className="rounded-sm border border-gray-200 px-4 py-2 text-sm font-semibold text-navy-700 dark:border-white/10 dark:text-white"
          >
            Discard changes
          </button>
        )}
      </div>
    </Card>
  );
}
